import { useEffect, useState } from "react";
import BACKEND_URL from "../../config/config";
import { useLanguage } from "../../context/LanguageContext";
import Tooltip from "../../utils/tooltip";

const initialForm = {
  defaultLanguage: "es",
  expectedAttendance: "",
  emissionsEnabled: false,
  fixedEmissions: {
    electricity: "",
    gas: "",
    logistics: "",
  },
  paymentMode: "free",
  currency: "ARS",
  priceAmount: "0",
  questionsEnabled: false,
  maintenancePageEnabled: false,
};

const Config = () => {
  const { language } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);

  const t = {
    EN: {
      loadError: "The configuration could not be loaded.",
      loadErrorTitle: "Could not load configuration",
      loadErrorMessage: "Refresh the page or try again in a few minutes.",
      defaultLanguageRequired: "Select the default language.",
      wholeNumberRequired: "Enter an integer greater than or equal to 0.",
      validNumberRequired: "Enter a number greater than or equal to 0.",
      paymentModeRequired: "Select a payment mode.",
      currencyRequired: "Select a currency.",
      saveError: "The configuration could not be saved.",
      saveSuccessTitle: "Configuration saved",
      saveSuccessMessage: "Changes were saved successfully.",
      saveErrorTitle: "Could not save configuration",
      saveErrorMessage: "Review the data and try again.",
      accept: "Accept",
      pageTitle: "Event configuration view",
      pageSubtitle: "General event configuration and administrative access.",
      manageInvitationQuotas: "Manage invitation quotas",
      eventInfo: "Event information",
      defaultLanguage: "Default language",
      expectedAttendance: "What is the expected attendance?",
      expectedAttendancePlaceholder: "Number of people",
      eventEmissions: "Event emissions",
      measureEmissions: "Will emissions be measured at the event?",
      fixedEmissions: "What are the event's fixed emissions?",
      electricity: "Electricity",
      gas: "Gas",
      logistics: "Logistics",
      enterValue: "Enter value",
      eventQuestions: "Event questions",
      enableAdditionalQuestions: "Enable additional questions?",
      soldOutSettings: "Sold out screen",
      enableMaintenancePage: "Show sold out screen?",
      maintenancePageHint:
        "If enabled, visitors will see the sold out screen even before the attendance limit is reached.",
      eventMonetization: "Event monetization",
      isPaidEvent: "Is this a paid event?",
      paymentMode: "Which payment mode will be used?",
      free: "Free",
      paypalAndMercadopago: "Paypal and Mercadopago",
      registrationCost: "What is the registration cost?",
      currency: "Currency",
      amount: "Amount",
      saving: "Saving...",
      saveConfig: "Save configuration",
    },
    ES: {
      loadError: "No se pudo cargar la configuración.",
      loadErrorTitle: "No se pudo cargar la configuración",
      loadErrorMessage: "Actualizá la página o intentá nuevamente en unos minutos.",
      defaultLanguageRequired: "Seleccioná el idioma por defecto.",
      wholeNumberRequired: "Ingresá un número entero mayor o igual a 0.",
      validNumberRequired: "Ingresá un número mayor o igual a 0.",
      paymentModeRequired: "Seleccioná el modo de pago.",
      currencyRequired: "Seleccioná una moneda.",
      saveError: "No se pudo guardar la configuración.",
      saveSuccessTitle: "Configuración guardada",
      saveSuccessMessage: "Los cambios se guardaron correctamente.",
      saveErrorTitle: "No se pudo guardar la configuración",
      saveErrorMessage: "Revisá los datos e intentá nuevamente.",
      accept: "Aceptar",
      pageTitle: "Vista configuración del evento",
      pageSubtitle: "Configuración general del evento y accesos administrativos.",
      manageInvitationQuotas: "Gestionar cupos de invitación",
      eventInfo: "Información del evento",
      defaultLanguage: "Idioma por defecto",
      expectedAttendance: "¿Cuál es la convocatoria esperada?",
      expectedAttendancePlaceholder: "Cantidad de personas",
      eventEmissions: "Emisiones del evento",
      measureEmissions: "¿Medirán emisiones en el evento?",
      fixedEmissions: "¿Cuáles son las emisiones fijas del evento?",
      electricity: "Luz",
      gas: "Gas",
      logistics: "Logística",
      enterValue: "Ingrese valor",
      eventQuestions: "Preguntas del evento",
      enableAdditionalQuestions: "¿Habilitar preguntas adicionales?",
      soldOutSettings: "Pantalla sold out",
      enableMaintenancePage: "¿Mostrar pantalla sold out?",
      maintenancePageHint:
        "Si se habilita, los visitantes verán la pantalla sold out aunque todavía no se haya alcanzado el cupo.",
      eventMonetization: "Monetización del evento",
      isPaidEvent: "¿Es un evento pago?",
      paymentMode: "¿Qué modo de pago se utilizará?",
      free: "Gratis",
      paypalAndMercadopago: "Paypal y Mercadopago",
      registrationCost: "¿Cuál es el costo de inscripción?",
      currency: "Moneda",
      amount: "Monto",
      saving: "Guardando...",
      saveConfig: "Guardar configuración",
    },
  }[language];

  const showModal = (type, title, message) => {
    setModal({ type, title, message });
  };

  useEffect(() => {
    let cancelled = false;

    const loadConfig = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/config/admin`);
        if (!res.ok) {
          throw new Error(t.loadError);
        }

        const data = await res.json();
        if (cancelled) return;

        setForm({
          defaultLanguage: String(data?.default_language || "es").toLowerCase(),
          expectedAttendance:
            data?.expected_attendance !== undefined &&
              data?.expected_attendance !== null
              ? String(data.expected_attendance)
              : "",
          emissionsEnabled: Boolean(data?.enable_emission),
          fixedEmissions: {
            electricity:
              data?.fixed_emissions?.electricity !== undefined &&
                data?.fixed_emissions?.electricity !== null
                ? String(data.fixed_emissions.electricity)
                : "",
            gas:
              data?.fixed_emissions?.gas !== undefined &&
                data?.fixed_emissions?.gas !== null
                ? String(data.fixed_emissions.gas)
                : "",
            logistics:
              data?.fixed_emissions?.logistics !== undefined &&
                data?.fixed_emissions?.logistics !== null
                ? String(data.fixed_emissions.logistics)
                : "",
          },
          paymentMode: data?.payment_mode || "free",
          currency: data?.currency || "ARS",
          priceAmount:
            data?.price_amount !== undefined && data?.price_amount !== null
              ? String(data.price_amount)
              : "0",
          questionsEnabled: Boolean(data?.enable_questions),
          maintenancePageEnabled: Boolean(data?.maintenance_page_enabled),
        });
      } catch (error) {
        console.error(error);
        showModal(
          "error",
          t.loadErrorTitle,
          t.loadErrorMessage
        );
      }
    };

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, [t.loadError, t.loadErrorMessage, t.loadErrorTitle]);

  const isWholeNumber = (value) => {
    if (value === "") return false;
    const numberValue = Number(value);
    return Number.isInteger(numberValue) && numberValue >= 0;
  };

  const isValidNumber = (value) => {
    if (value === "") return false;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue >= 0;
  };

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const updateFixedEmission = (field, value) => {
    setForm((prev) => ({
      ...prev,
      fixedEmissions: {
        ...prev.fixedEmissions,
        [field]: value,
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [`fixedEmissions.${field}`]: "",
    }));
  };

  const handlePaymentEnabledChange = (checked) => {
    setForm((prev) => ({
      ...prev,
      paymentMode: checked ? "mercadopago" : "free",
      currency: checked ? "ARS" : "ARS",
      priceAmount: checked ? (prev.priceAmount === "0" ? "" : prev.priceAmount) : "0",
    }));

    setErrors((prev) => ({
      ...prev,
      paymentMode: "",
      currency: "",
      priceAmount: "",
    }));
  };

  const handlePaymentModeChange = (value) => {
    const nextCurrency =
      value === "paypal"
        ? "USD"
        : value === "free" || value === "mercadopago"
          ? "ARS"
          : form.currency || "ARS";

    setForm((prev) => ({
      ...prev,
      paymentMode: value,
      currency: nextCurrency,
      priceAmount: value === "free" ? "0" : prev.priceAmount === "0" ? "" : prev.priceAmount,
    }));

    setErrors((prev) => ({
      ...prev,
      paymentMode: "",
      currency: "",
      priceAmount: "",
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.defaultLanguage) {
      newErrors.defaultLanguage = t.defaultLanguageRequired;
    }

    if (!isWholeNumber(form.expectedAttendance)) {
      newErrors.expectedAttendance = t.wholeNumberRequired;
    }

    if (form.emissionsEnabled) {
      if (!isValidNumber(form.fixedEmissions.electricity)) {
        newErrors["fixedEmissions.electricity"] = t.validNumberRequired;
      }

      if (!isValidNumber(form.fixedEmissions.gas)) {
        newErrors["fixedEmissions.gas"] = t.validNumberRequired;
      }

      if (!isValidNumber(form.fixedEmissions.logistics)) {
        newErrors["fixedEmissions.logistics"] = t.validNumberRequired;
      }
    }

    if (!form.paymentMode) {
      newErrors.paymentMode = t.paymentModeRequired;
    }

    if (form.paymentMode !== "free") {
      if (!form.currency) {
        newErrors.currency = t.currencyRequired;
      }

      if (!isValidNumber(form.priceAmount)) {
        newErrors.priceAmount = t.validNumberRequired;
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    default_language: form.defaultLanguage,
    expected_attendance: Number(form.expectedAttendance),
    enable_emission: form.emissionsEnabled,
    fixed_emissions: {
      electricity: form.emissionsEnabled
        ? Number(form.fixedEmissions.electricity)
        : 0,
      gas: form.emissionsEnabled ? Number(form.fixedEmissions.gas) : 0,
      logistics: form.emissionsEnabled
        ? Number(form.fixedEmissions.logistics)
        : 0,
    },
    payment_mode: form.paymentMode,
    currency: form.paymentMode === "free" ? "ARS" : form.currency,
    price_amount: form.paymentMode === "free" ? 0 : Number(form.priceAmount),
    enable_questions: form.questionsEnabled,
    maintenance_page_enabled: form.maintenancePageEnabled,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    const payload = buildPayload();

    try {
      setLoading(true);

      const res = await fetch(`${BACKEND_URL}/config/admin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || t.saveError);
      }

      console.log("Payload para backend:", payload);

      showModal(
        "success",
        t.saveSuccessTitle,
        t.saveSuccessMessage
      );
    } catch (error) {
      console.error(error);
      showModal(
        "error",
        t.saveErrorTitle,
        t.saveErrorMessage
      );
    } finally {
      setLoading(false);
    }
  };

  const isPaidEvent = form.paymentMode !== "free";

  return (
    <div className="container">
      {modal && (
        <div className="configModalOverlay" role="dialog" aria-modal="true">
          <div className="configModal">
            <h2>{modal.title}</h2>
            <p>{modal.message}</p>
            <button
              type="button"
              onClick={() => setModal(null)}
              className={modal.type === "error" ? "btnPrint" : undefined}
            >
              {t.accept}
            </button>
          </div>
        </div>
      )}

      <div className="configPageHeader">
        <h1>{t.pageTitle}</h1>
        <p>{t.pageSubtitle}</p>

      </div>

      <form onSubmit={handleSubmit}>
        <section className="card">
          <h2>{t.eventInfo}</h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)",
              gap: "16px",
              alignItems: "start",
            }}
          >
            <div>
              <label>{t.defaultLanguage}</label>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  margin: "8px 0 20px",
                }}
              >
                <span style={{ opacity: form.defaultLanguage === "es" ? 1 : 0.5 }}>
                  ES
                </span>

                <label className="switch">
                  <input
                    type="checkbox"
                    checked={form.defaultLanguage === "en"}
                    onChange={(e) =>
                      updateField("defaultLanguage", e.target.checked ? "en" : "es")
                    }
                  />
                  <span className="slider"></span>
                </label>

                <span style={{ opacity: form.defaultLanguage === "en" ? 1 : 0.5 }}>
                  EN
                </span>
              </div>

              {errors.defaultLanguage && (
                <p className="required">{errors.defaultLanguage}</p>
              )}
            </div>

            <div>
              <label>
                <div>{t.enableMaintenancePage}</div>
                <Tooltip text={t.maintenancePageHint} />
              </label>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  margin: "8px 0 20px",
                }}
              >
                <span style={{ opacity: form.maintenancePageEnabled ? 0.5 : 1 }}>
                  OFF
                </span>

                <label className="switch">
                  <input
                    type="checkbox"
                    checked={form.maintenancePageEnabled}
                    onChange={(e) =>
                      updateField("maintenancePageEnabled", e.target.checked)
                    }
                  />
                  <span className="slider"></span>
                </label>

                <span style={{ opacity: form.maintenancePageEnabled ? 1 : 0.5 }}>
                  ON
                </span>
              </div>
            </div>

            <div>
              <label>{t.expectedAttendance}</label>
              <input
                type="number"
                min="0"
                value={form.expectedAttendance}
                onChange={(e) =>
                  updateField("expectedAttendance", e.target.value)
                }
                placeholder={t.expectedAttendancePlaceholder}
              />
              {errors.expectedAttendance && (
                <p className="required">{errors.expectedAttendance}</p>
              )}
            </div>
          </div>
        </section>

        <section className="card">
          <h2>{t.eventEmissions}</h2>

          <label
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <input
              style={{
                width: "20px",
                height: "20px",
              }}
              type="checkbox"
              checked={form.emissionsEnabled}
              onChange={(e) =>
                updateField("emissionsEnabled", e.target.checked)
              }
            />
            {t.measureEmissions}
          </label>

          <label>{t.fixedEmissions}</label>

          <div className="cont2Col">
            <div>
              <label>{t.electricity}</label>
              <input
                type="number"
                min="0"
                disabled={!form.emissionsEnabled}
                value={form.fixedEmissions.electricity}
                onChange={(e) =>
                  updateFixedEmission("electricity", e.target.value)
                }
                placeholder={t.enterValue}
              />
              {errors["fixedEmissions.electricity"] && (
                <p className="required">
                  {errors["fixedEmissions.electricity"]}
                </p>
              )}
            </div>

            <div>
              <label>{t.gas}</label>
              <input
                type="number"
                min="0"
                disabled={!form.emissionsEnabled}
                value={form.fixedEmissions.gas}
                onChange={(e) => updateFixedEmission("gas", e.target.value)}
                placeholder={t.enterValue}
              />
              {errors["fixedEmissions.gas"] && (
                <p className="required">{errors["fixedEmissions.gas"]}</p>
              )}
            </div>

            <div>
              <label>{t.logistics}</label>
              <input
                type="number"
                min="0"
                disabled={!form.emissionsEnabled}
                value={form.fixedEmissions.logistics}
                onChange={(e) =>
                  updateFixedEmission("logistics", e.target.value)
                }
                placeholder={t.enterValue}
              />
              {errors["fixedEmissions.logistics"] && (
                <p className="required">
                  {errors["fixedEmissions.logistics"]}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="card">
          <h2>{t.eventQuestions}</h2>

          <label
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <input
              style={{
                width: "20px",
                height: "20px",
              }}
              type="checkbox"
              checked={form.questionsEnabled}
              onChange={(e) =>
                updateField("questionsEnabled", e.target.checked)
              }
            />
            {t.enableAdditionalQuestions}
          </label>
        </section>

        <section className="card">
          <h2>{t.eventMonetization}</h2>

          <label
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <input
              style={{
                width: "20px",
                height: "20px",
              }}
              type="checkbox"
              checked={isPaidEvent}
              onChange={(e) => handlePaymentEnabledChange(e.target.checked)}
            />
            {t.isPaidEvent}
          </label>

          <label>{t.paymentMode}</label>
          <select
            value={form.paymentMode}
            disabled={!isPaidEvent}
            onChange={(e) => handlePaymentModeChange(e.target.value)}
          >
            <option value="free">{t.free}</option>
            <option value="paypal">Paypal</option>
            <option value="mercadopago">Mercadopago</option>
            <option value="paypal_mercadopago">{t.paypalAndMercadopago}</option>
          </select>
          {errors.paymentMode && (
            <p className="required">{errors.paymentMode}</p>
          )}

          <label>{t.registrationCost}</label>

          <div className="cont2Col">
            <div>
              <label>{t.currency}</label>
              <select
                disabled={!isPaidEvent || form.paymentMode !== "paypal_mercadopago"}
                value={form.currency}
                onChange={(e) => updateField("currency", e.target.value)}
              >
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </select>
              {errors.currency && (
                <p className="required">{errors.currency}</p>
              )}
            </div>

            <div>
              <label>{t.amount}</label>
              <input
                type="number"
                min="0"
                disabled={!isPaidEvent}
                value={form.priceAmount}
                onChange={(e) => updateField("priceAmount", e.target.value)}
                placeholder={form.currency}
              />
              {errors.priceAmount && (
                <p className="required">{errors.priceAmount}</p>
              )}
            </div>
          </div>
        </section>

        <button type="submit" disabled={loading}>
          {loading ? t.saving : t.saveConfig}
        </button>
      </form>
    </div>
  );
};

export default Config;
